import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button/Button";
import Checkbox from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { ValidatorRuleType } from "@tKgpco/Common/Utilities/validate";
import { useForm } from "@tKgpco/Custom/Hooks/useForm";
import editAccessorial from "@tKgpco/Handlers/Accessorials/UpdateAccessorial";
import { UpdateAccessorialApiParameters } from "@tKgpco/Services/AccessorialsService";
import { Accessorial } from "@tKgpco/Store/Data/Accessorials/AccessorialsState";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    editAccessorial,
};

interface CustomWidgetStyles {
    mainGridContainer?: GridContainerProps;
    heading?: TypographyPresentationProps;
}

const styles: CustomWidgetStyles = {
    mainGridContainer: {
        offsetCss: css`
            max-width: 700px;
            margin: 0 auto;
            background: #f1f6fb;
            border: 1px solid #e1e7ed;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: 100%;
                        `,
                        css``,
                        css``,
                        css``,
                    ],
                    "max",
                );
            }}
        `,
    },
    heading: {
        css: css`
            text-transform: uppercase;
            font-size: 22px;
            color: #0b70b7;
            border-bottom: 1px solid #0b70b7;
            padding-bottom: 5px;
            width: 100%;
        `,
    },
};

const EMPTY_STRING = "";

interface AccessorialsFormValues {
    master: string;
    name: string;
    type: string;
    code: string;
    description: string;
    enabled: boolean;
    carrierNoteOnly: boolean;
    carrierNotes: string;
}

const formValues: AccessorialsFormValues = {
    master: EMPTY_STRING,
    name: EMPTY_STRING,
    type: EMPTY_STRING,
    code: EMPTY_STRING,
    description: EMPTY_STRING,
    enabled: false,
    carrierNoteOnly: false,
    carrierNotes: EMPTY_STRING,
};

const initialErrorState: Omit<AccessorialsFormValues, "enabled" | "carrierNoteOnly"> = {
    master: EMPTY_STRING,
    name: EMPTY_STRING,
    type: EMPTY_STRING,
    code: EMPTY_STRING,
    description: EMPTY_STRING,
    carrierNotes: EMPTY_STRING,
};

const validatorConfig: { [key in keyof AccessorialsFormValues]: ValidatorRuleType } = {
    master: {
        required: true,
        label: "Master",
        requiredMsg: "Master is required",
    },
    code: {
        required: true,
        label: "Code",
        requiredMsg: "Code is required",
    },
    description: {
        required: true,
        label: "Description",
        requiredMsg: "Description is required",
    },
    name: {
        required: true,
        label: "Name",
        requiredMsg: "Name is required",
    },
    type: {
        required: true,
        label: "Type",
        requiredMsg: "Type is required",
    },
    enabled: {
        required: false,
        label: "Enabled",
    },
    carrierNoteOnly: {
        required: false,
        label: "Carrier Note Only",
    },
    carrierNotes: {
        // required: false,
        label: "Carrier Notes",
    },
};

interface OwnProps {
    accessorial: Accessorial;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const EditAccessorialsForm: React.FC<Props> = ({ editAccessorial, accessorial, history }) => {
    const {
        fieldChangeHandler,
        validateField,
        updateFormState,
        updateFormErrors,
        formState,
        formErrors,
        isSubmitDisabled,
    } = useForm({
        formValues: {
            code: accessorial.tI_Code,
            description: accessorial.tI_Description,
            master: accessorial.master,
            name: accessorial.name,
            type: accessorial.type,
            enabled: accessorial.enabled,
            carrierNoteOnly: accessorial.carrierNoteOnly,
            carrierNotes: accessorial.carrierNotes,
        },
        initialErrorState: {},
        validatorConfig,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const handleUpdate = () => {
        setIsSubmitting(true);
        const params: UpdateAccessorialApiParameters = {
            Id: accessorial.id,
            TI_Code: formState.code,
            TI_Description: formState.description,
            Enabled: formState.enabled,
            Master: formState.master,
            Name: formState.name,
            Type: formState.type,
            CarrierNoteOnly: formState.carrierNoteOnly,
            CarrierNotes: formState.carrierNotes,
        };
        editAccessorial({
            ...params,
            onSuccess: () => {
                setIsSubmitting(false);
                history.push("/MyAccount/Accessorials");
                toasterContext.addToast({
                    messageType: "success",
                    body: translate("Accessorial updated successfully."),
                    timeoutLength: 4000,
                });
            },
            onError(message: any) {
                setIsSubmitting(false);

                toasterContext.addToast({
                    messageType: "danger",
                    body: message || translate("An error occurred while editing the accessorial."),
                    timeoutLength: 3600000,
                });
            },
        });
    };

    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem width={12}>
                <Typography as="h2" {...styles.heading}>
                    {translate("Update Accessorial")}
                </Typography>
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Master")}
                    name="master"
                    value={formState.master}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.master && formErrors.master}
                    iconProps={{
                        color: "danger",
                        src: formErrors.master ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Name")}
                    name="name"
                    value={formState.name}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.name && formErrors.name}
                    iconProps={{
                        color: "danger",
                        src: formErrors.name ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Type")}
                    name="type"
                    value={formState.type}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.type && formErrors.type}
                    iconProps={{
                        color: "danger",
                        src: formErrors.type ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Code")}
                    name="code"
                    value={formState.code}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.code && formErrors.code}
                    iconProps={{
                        color: "danger",
                        src: formErrors.code ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Description")}
                    name="description"
                    value={formState.description}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.description && formErrors.description}
                    iconProps={{
                        color: "danger",
                        src: formErrors.description ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem width={12}>
                <Checkbox
                    labelPosition="right"
                    checked={formState.enabled}
                    onChange={(_, value: boolean) =>
                        updateFormState(() => ({
                            ...formState,
                            enabled: !formState.enabled,
                        }))
                    }
                >
                    {translate("Enabled")}
                </Checkbox>
            </GridItem>
            <GridItem width={12}>
                <Checkbox
                    labelPosition="right"
                    checked={formState.carrierNoteOnly}
                    onChange={(_, value: boolean) =>
                        updateFormState(() => ({
                            ...formState,
                            carrierNoteOnly: !formState.carrierNoteOnly,
                        }))
                    }
                >
                    {translate("Carrier Note Only")}
                </Checkbox>
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Carrier Notes")}
                    name="carrierNotes"
                    value={formState.carrierNotes}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.carrierNotes && formErrors.carrierNotes}
                    iconProps={{
                        color: "danger",
                        src: formErrors.carrierNotes ? AlertTriangle : undefined,
                    }}
                />
            </GridItem>
            <GridItem>
                <Button onClick={handleUpdate} disabled={isSubmitDisabled || isSubmitting}>
                    Submit
                </Button>
            </GridItem>
        </GridContainer>
    );
};

export default connect(null, mapDispatchToProps)(withHistory(EditAccessorialsForm));
